import { useEffect, useMemo, useState } from "react";
import { ClipboardPenLine, Plus, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { todayET } from "@/lib/dates";

const money = (value) => (Number(value) % 1 === 0 ? `$${Number(value).toLocaleString("en-US")}` : `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2 })}`);
const day = (value) => (value ? new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

export const ProgressNotesPanel = ({ initialClientId = "", onSubmitted }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [clientId, setClientId] = useState(initialClientId);
  const [completedOn, setCompletedOn] = useState(todayET());
  const [checked, setChecked] = useState({});
  const [added, setAdded] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const load = () => api.get("/progress-notes/mine").then((r) => setData(r.data)).catch((e) => setError(formatApiError(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (data && !clientId && data.schools.length === 1) setClientId(data.schools[0].client_id);
  }, [data, clientId]);

  const school = useMemo(() => data?.schools.find((s) => s.client_id === clientId) || null, [data, clientId]);
  const selected = useMemo(() => [...(school?.students || []).filter((s) => checked[s]), ...added], [school, checked, added]);
  const count = selected.length;
  const hours = count * (data?.note_hours || 0.5);
  const estimate = school?.hourly_rate ? `Progress notes: ${count} × 0.5 hr × ${money(school.hourly_rate)} = ${money(hours * school.hourly_rate)}` : "Your hourly rate is confirmed by TAG at approval.";

  const addInitials = () => {
    const value = draft.trim();
    if (!value) return;
    const normalized = value.replace(/[^A-Za-z]/g, "").toUpperCase().split("").join(".") + ".";
    if (normalized.replace(/\./g, "").length < 2) { setError("Initials need at least 2 letters, like J.D."); return; }
    if ((school?.students || []).includes(normalized)) { setChecked((c) => ({ ...c, [normalized]: true })); setDraft(""); return; }
    if (!added.includes(normalized)) setAdded((a) => [...a, normalized]);
    setDraft("");
    setError("");
  };

  async function submitNotes(event) {
    event.preventDefault();
    if (!count) { setError("Check off at least one student."); return; }
    setBusy(true);
    try {
      const response = await api.post("/progress-notes", { client_id: clientId, completed_on: completedOn, students: selected });
      setDone(response.data);
      setChecked({});
      setAdded([]);
      setError("");
      await load();
      onSubmitted?.(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="provider-report-panel progress-notes-panel" data-testid="progress-notes-panel">
      <div className="provider-queue-heading"><ClipboardPenLine size={18} /><div><h2>Progress notes</h2><p>Each approved progress note pays 30 minutes at your hourly rate for that school.</p></div></div>
      <p className="progress-notes-notice" data-testid="progress-notes-no-clock-in">{data?.notice || "Do not clock in on Connecteam to write progress notes. This submission is how you are paid for them."}</p>
      {error && <p className="form-message error-message" data-testid="progress-notes-error">{error}</p>}
      {done && <p className="form-message success-message" data-testid="progress-notes-success">Submitted — {done.summary} at {done.client_name} sent to TAG for approval{done.flagged_count ? ` · ${done.flagged_count} student${done.flagged_count === 1 ? "" : "s"} flagged for TAG review` : ""}.</p>}
      {!data && !error && <p className="projection-loading" data-testid="progress-notes-loading">Loading your schools…</p>}
      {data && !data.schools.length && <p className="provider-empty-state" data-testid="progress-notes-no-schools">Progress notes can be submitted only for schools where you have an active assignment — none is active right now.</p>}
      {data && data.schools.length > 0 && (
        <form className="provider-report-form progress-notes-form" data-testid="progress-notes-form" onSubmit={submitNotes}>
          <label>School<select data-testid="progress-notes-school-select" value={clientId} onChange={(e) => { setClientId(e.target.value); setChecked({}); setAdded([]); }} required><option value="">Select school</option>{data.schools.map((s) => <option key={s.client_id} value={s.client_id}>{s.client_name}</option>)}</select></label>
          <label>Date notes were completed<input data-testid="progress-notes-date-input" type="date" max={todayET()} value={completedOn} onChange={(e) => setCompletedOn(e.target.value)} required /></label>
          <div className="progress-notes-count" data-testid="progress-notes-summary"><strong>{count} note{count === 1 ? "" : "s"} = {hours.toFixed(1)} hours</strong><small data-testid="progress-notes-estimate">{school ? estimate : "Pick a school to see your pay line."}</small></div>
          {school && (
            <div className="provider-report-wide progress-notes-students" data-testid="progress-notes-students">
              <span className="field-label">Students you wrote progress notes for (initials)</span>
              {school.students.length ? (
                <div className="progress-notes-grid">
                  {school.students.map((initials) => (
                    <label key={initials} className={`progress-note-student ${checked[initials] ? "is-checked" : ""}`} data-testid={`progress-notes-student-${initials.replace(/\./g, "")}`}>
                      <input type="checkbox" checked={Boolean(checked[initials])} onChange={(e) => setChecked((c) => ({ ...c, [initials]: e.target.checked }))} /> {initials}
                    </label>
                  ))}
                </div>
              ) : <small className="pairing-meta" data-testid="progress-notes-no-students">No students listed for this school yet — add their initials below. New initials are flagged for TAG review.</small>}
              {added.length > 0 && (
                <div className="progress-notes-grid" data-testid="progress-notes-added">
                  {added.map((initials) => (
                    <span key={initials} className="progress-note-student is-checked is-added" data-testid={`progress-notes-added-${initials.replace(/\./g, "")}`}>{initials} <em>New · TAG review</em><button type="button" aria-label={`Remove ${initials}`} onClick={() => setAdded((a) => a.filter((x) => x !== initials))}><X size={12} /></button></span>
                  ))}
                </div>
              )}
              <div className="progress-notes-add">
                <input data-testid="progress-notes-add-input" placeholder="Student not listed? Add initials, e.g. J.D." value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addInitials(); } }} />
                <button className="button button-secondary" data-testid="progress-notes-add-button" type="button" onClick={addInitials}><Plus size={14} /> Add student</button>
              </div>
            </div>
          )}
          <button className="button button-primary" data-testid="submit-progress-notes-button" disabled={busy || !count || !clientId}><ClipboardPenLine size={16} /> {busy ? "Submitting…" : `Submit ${count || ""} progress note${count === 1 ? "" : "s"} for approval`}</button>
        </form>
      )}
      {data && <SubmissionList submissions={data.submissions} />}
    </section>
  );
};

const SubmissionList = ({ submissions }) => (
  <div className="provider-submitted-reports" data-testid="progress-notes-submissions">
    {submissions.map((item) => (
      <div key={item.id} className="progress-note-submission" data-testid={`progress-notes-submission-${item.id}`}>
        <strong>{item.client_name} · {day(item.completed_on)}</strong>
        <span>{item.summary} · <em className={`status-chip ${item.status === "Approved" ? "status-active" : item.status === "Returned" ? "status-inactive" : "status-pending"}`} data-testid={`progress-notes-status-${item.id}`}>{item.status === "Submitted" ? "Awaiting TAG approval" : item.status}</em></span>
        <span data-testid={`progress-notes-line-${item.id}`}>{item.line}</span>
        {item.flagged_count > 0 && item.status !== "Approved" && <span className="progress-note-flag">{item.flagged_count} flagged for TAG review</span>}
        {item.review_note && <span className="progress-note-review-note">TAG: {item.review_note}</span>}
      </div>
    ))}
    {!submissions.length && <p className="provider-empty-state">No progress notes submitted yet.</p>}
  </div>
);
